import dayjs from 'dayjs'

import type { CampaignDetails, SelectedTag } from '~publish/legacy/tags/types'
import type { FacebookMentionDbEntry } from '~publish/legacy/editor/plugins/facebook-mention/nodes'
import type { LinkedInAnnotationEntity } from '~publish/legacy/editor/plugins/linkedin-annotations/nodes'
import type { UserTag } from '~publish/legacy/composer/composer/stores/types'
import type { PublishingNote } from '~publish/legacy/post/graphqlTypes'
import type { RpcUpdateStatistics } from '~publish/legacy/post/types'
import type { ParsedChannelLink } from '~publish/legacy/utils/channels/types'

import { channelDataParser } from './channelDataParser'
import type { LegacyChannelData } from './channelDataParser'
import {
  parseFacebookEntities,
  parseThreadsLinks,
  parseTwitterLinks,
} from './linkParsing'
import { getDateString, isInThePast } from '../formatters/index'

type LegacyPostMedia = {
  picture?: string
  photo?: string
  thumbnail?: string
  video?:
    | string
    | {
        details?: {
          transcoded_location?: string
        }
      }
  document?: string | { id?: string }
  link?: string
  title?: string
  expanded_link?: string
  description?: string
  preview?: string
}

type LegacyRetweet = {
  profile_name?: string
  display_name?: string
  username?: string
  user_name?: string
  avatars?: { https?: string }
  avatar_https?: string
  text?: string
  comment?: string
}

type LegacyPostUser = {
  email: string
  name?: string
  gravatar?: string
  avatar: string
}

type LegacyPostError =
  | string
  | {
      text?: string
      link?: string
    }

type LegacyAnnotation = Omit<LinkedInAnnotationEntity, 'start' | 'length'> & {
  start: number | string
  length: number | string
}

type LegacyNote = {
  _id: string
  created_at: number | string
  edited_at?: number | null
  text: string
  type: PublishingNote['type']
  author: {
    email: string
    name?: string
    avatar: string
    _id: string
  }
}

type ParsedNote = PublishingNote & {
  author: PublishingNote['author'] & { name?: string }
}

type ParsedPostDetails = {
  postAction: string
  isRetweet: boolean
  error: string | null
  errorLink: string | null
  errorLabel: string | null
  isCustomScheduled: boolean
  isInstagramReminder: boolean
  isPendingTiktokPost: boolean
}

type RetweetProfileInfo = {
  name: string
  handle: string
  avatarUrl: string
}

type ParsedPostUser = LegacyPostUser & {
  id: LegacyPostData['user_id']
}

type ParsedLinkAttachment = {
  title: string | undefined
  url: string | undefined
  description: string | undefined
  thumbnailUrl: string | undefined
}

type ParsedPostContent = {
  imageSrc: string | undefined
  videoSrc: string | undefined
  imageUrls: Array<string | undefined>
  linkAttachment: ParsedLinkAttachment
  isReelPost: boolean
  isFacebookReelPost: boolean
  isFacebookStoryPost: boolean
  isStoryPost: boolean
  isShortPost: boolean
  links: ParsedChannelLink[]
  retweetComment: string | undefined
  retweetCommentLinks: ParsedChannelLink[]
  retweetProfile: RetweetProfileInfo | undefined
  text: string | undefined
  type: 'text' | 'video' | 'document' | 'link' | 'image' | 'multipleImage'
}

type ParsedPost = {
  day: string | undefined
  id: string | undefined
  status: string | undefined
  createdAt: number | undefined
  entities: FacebookMentionDbEntry[] | undefined
  profileId: string | undefined
  isConfirmingDelete: boolean | undefined
  isDeleting: boolean | undefined
  isWorking: boolean | undefined
  isMoving: boolean | undefined
  isPastDue: boolean
  imageSrc: string | undefined
  videoSrc: string | undefined
  imageUrls: Array<string | undefined>
  links: ParsedChannelLink[]
  profileTimezone: string | undefined
  linkAttachment: ParsedLinkAttachment
  needsApproval: boolean | undefined
  postDetails: ParsedPostDetails
  profile_service: string | undefined
  retweet: LegacyRetweet | undefined
  retweetComment: string | undefined
  retweetCommentLinks: ParsedChannelLink[]
  retweetProfile: RetweetProfileInfo | undefined
  isSent: boolean
  isPastReminder: boolean
  isReminder: boolean | null
  isDraft: boolean
  title: string | undefined
  source_url: string | undefined
  text: string | undefined
  type: ParsedPostContent['type']
  media: LegacyPostMedia
  extra_media: LegacyPostMedia[] | undefined
  subprofile_id: string | undefined
  due_at: number | undefined
  scheduled_at: number | undefined
  scheduledAt: number | undefined
  sharedNext: boolean | undefined
  pinned: boolean | undefined
  isFixed: boolean
  statistics: [] | RpcUpdateStatistics
  service_user_tags: UserTag[] | undefined
  annotations: LinkedInAnnotationEntity[] | undefined
  user: ParsedPostUser | undefined
  serviceLink: string | null | undefined
  channelData: LegacyChannelData | null
  serviceUpdateId: string | undefined
  dueTime: string | undefined
  sharedBy: string | undefined
  campaignId: string | undefined
  campaignDetails: CampaignDetails | null
  notes: ParsedNote[]
  postContent: ParsedPostContent
  thread: LegacyPostData[] | undefined
  updateType: string | undefined
  via: string | null
  tags: SelectedTag[]
}

export type LegacyPostData = {
  id?: string
  _id?: string
  day?: string
  text?: string
  title?: string
  status?: string
  update_type?: string
  profile_service?: string
  profile_id?: string
  profile_timezone?: string
  twentyfour_hour_time?: boolean
  created_at?: number
  sent_at?: number
  due_at?: number
  scheduled_at?: number
  scheduling_type?: string
  can_send_direct?: boolean
  pinned?: boolean
  isDeleting?: boolean
  requestingDraftAction?: boolean
  isMoving?: boolean
  needs_approval?: boolean
  source_url?: string
  service_link?: string
  service_update_id?: string
  due_time?: string
  shared_by?: string
  shared_next?: boolean
  subprofile_id?: string
  campaign_id?: string
  campaign_details?: CampaignDetails
  media?: LegacyPostMedia
  extra_media?: LegacyPostMedia[]
  retweet?: LegacyRetweet
  user?: LegacyPostUser
  user_id?: string | number
  error?: LegacyPostError | null
  error_object?: { link_text?: string }
  entities?: FacebookMentionDbEntry[]
  annotations?: LegacyAnnotation[]
  notes?: Array<LegacyNote | PublishingNote>
  channel_data?: LegacyChannelData | null
  statistics?: RpcUpdateStatistics | []
  service_user_tags?: UserTag[]
  tags?: SelectedTag[]
  thread?: LegacyPostData[]
  via?: string
}

const getImageUrls = (post: LegacyPostData): Array<string | undefined> => {
  if (!(post.media && post.media.picture && post.extra_media)) return []
  const imageUrls = post.extra_media.map((media) => media.photo)
  imageUrls.unshift(post.media.picture)
  return imageUrls
}

const isFacebookReelPost = ({ post }: { post: LegacyPostData }): boolean =>
  post.update_type === 'facebook_reel'
const isFacebookStoryPost = ({ post }: { post: LegacyPostData }): boolean =>
  post.update_type === 'facebook_story'
const isReelPost = ({ post }: { post: LegacyPostData }): boolean =>
  post.update_type === 'reels'
const isShortPost = ({ post }: { post: LegacyPostData }): boolean =>
  post.update_type === 'short'
const isStoryPost = ({ post }: { post: LegacyPostData }): boolean =>
  post.update_type === 'story'
const getElementName = ({ post }: { post: LegacyPostData }): string => {
  let name = 'post'
  if (isReelPost({ post })) {
    name = 'reel'
  }
  if (isFacebookReelPost({ post })) {
    name = 'facebook_reel'
  }
  if (isFacebookStoryPost({ post })) {
    name = 'facebook_story'
  }
  if (isStoryPost({ post })) {
    name = 'story'
  }
  return name
}

const getPostActionString = ({ post }: { post: LegacyPostData }): string => {
  const timestampToConvert = post.sent_at || post.due_at
  const element = getElementName({ post })
  // due_at set to 0 when user has no scheduled posting times
  if (timestampToConvert === undefined || timestampToConvert === 0) {
    return 'No Time Set'
  }
  const dateString = getDateString(timestampToConvert, post.profile_timezone, {
    twentyFourHourTime: post.twentyfour_hour_time,
  })
  if (post.profile_service === 'tiktok' && post.status === 'pending_tiktok') {
    return `This post was sent to Tiktok on ${dateString}`
  }
  // to run in every situation except when can_send_direct is explicitly false.
  // if pinned is explicitly set to true, then post is not custom scheduled
  if (
    post.scheduled_at &&
    post.can_send_direct !== false &&
    post.pinned !== true
  ) {
    return `This ${element} ${
      post.sent_at ? 'was' : 'is'
    } custom scheduled for ${dateString}.`
  }
  if (
    post.profile_service === 'instagram' &&
    !post.can_send_direct &&
    !post.sent_at
  ) {
    return `You will receive a reminder on ${dateString} when it's time to post.`
  }
  return `This ${element} ${
    post.sent_at ? 'was' : 'will be'
  } sent ${dateString}.`
}

const getPostError = ({
  error,
  status,
}: {
  error?: LegacyPostError | null
  status?: string
}): string | null => {
  if (status !== 'error') {
    return null
  }
  return typeof error === 'object' && error !== null
    ? error.text || ''
    : error || ''
}

const getPostDetails = ({
  post,
}: {
  post: LegacyPostData
}): ParsedPostDetails => ({
  postAction: getPostActionString({ post }),
  isRetweet: post.retweet !== undefined,
  error: getPostError({ error: post.error, status: post.status }),
  errorLink:
    post.status === 'error' &&
    typeof post.error === 'object' &&
    post.error?.link
      ? post.error.link
      : null,
  errorLabel:
    post.status === 'error' && post.error_object && post.error_object.link_text
      ? post.error_object.link_text
      : null,
  isCustomScheduled: !!post.scheduled_at,
  isInstagramReminder:
    post.profile_service === 'instagram' && !post.can_send_direct,
  isPendingTiktokPost: post.status === 'pending_tiktok',
})

const getRetweetProfileInfo = (
  post: LegacyPostData,
): RetweetProfileInfo | undefined => {
  const { retweet } = post
  if (!retweet) {
    return undefined
  }
  return {
    name: retweet.profile_name || retweet.display_name || 'UNKNOWN',
    handle: `@${retweet.username || retweet.user_name || 'UNKNOWN'}`,
    avatarUrl:
      (retweet.avatars && retweet.avatars.https) ||
      retweet.avatar_https ||
      'https://static.buffer.com/images/app/placeholder-twitter.png',
  }
}

const getPostType = ({
  post,
}: {
  post: LegacyPostData
}): 'text' | 'image' | 'multipleImage' | 'video' | 'document' | 'link' => {
  if (!post.media || post.retweet) {
    return 'text'
  }
  if (
    post.media &&
    post.media.picture &&
    !post.extra_media &&
    !post.media.document
  ) {
    return 'image'
  }
  if (post.media && post.media.picture && post.extra_media) {
    return 'multipleImage'
  }
  if (post.media && post.media.video) {
    return 'video'
  }
  if (post.media && post.media.document) {
    return 'document'
  }
  if (post.media && post.media.link) {
    return 'link'
  }
  return 'text'
}

const getUser = (post: LegacyPostData): ParsedPostUser | undefined => {
  if (!post.user) return
  return {
    email: post.user.email,
    name: post.user.name,
    gravatar: post.user.gravatar,
    avatar: post.user.avatar,
    id: post.user_id,
  }
}

const removeDuplicates = (links: ParsedChannelLink[]): ParsedChannelLink[] => {
  const linksByIndices = new Map<string, ParsedChannelLink>()
  links.forEach((link) => {
    const key = link.indices.join(',')
    if (!linksByIndices.has(key)) {
      linksByIndices.set(key, link)
    }
  })
  return Array.from(linksByIndices.values())
}

// TEMPORARY FIX
// TODO: Remove this once problem has been solved
// In a few rare cases, posts or drafts exist with
// a scheduled_at date set, without a due_at date set.
// This should never happen as the due_at property is
// used to actually publish posts to their channels.
// As a temporary patch for the frontend, if a post
// has a scheduled_at date, but no due_at date,
// set the due_at date equal to the scheduled_at date.
const getPostDueAt = (post: LegacyPostData): number | undefined => {
  if (post.due_at) return post.due_at
  if (post.scheduled_at) return post.scheduled_at
}

const parseVia = (via?: string | null): string | null => {
  // if via is falsy
  if (!via) {
    return null
  }
  if (via === 'api') {
    return 'Buffer'
  }
  return via.charAt(0).toUpperCase() + via.slice(1)
}

const parseAnnotations = (
  annotations?: LegacyAnnotation[] | null,
): LinkedInAnnotationEntity[] | undefined => {
  return annotations?.map((annotation) => {
    const { start, length } = annotation
    return { ...annotation, start: Number(start), length: Number(length) }
  })
}

const parseNotes = (
  notes: Array<LegacyNote | PublishingNote> = [],
): ParsedNote[] => {
  return notes.map((note) => {
    if ('createdAt' in note) {
      return note
    }

    const createdAt =
      typeof note.created_at === 'number'
        ? note.created_at
        : dayjs(note.created_at).unix()
    return {
      id: note._id,
      createdAt,
      editedAt: note.edited_at ?? null,
      text: note.text,
      type: note.type,
      author: {
        email: note.author.email,
        name: note.author.name,
        avatar: note.author.avatar,
        id: note.author._id,
      },
    }
  })
}

const postParser = (post: LegacyPostData): ParsedPost => {
  const media = post.media || {}
  const isVideo = media.video
  let retweetComment
  let text
  if (post.retweet) {
    text = post.retweet.text
    retweetComment = post.retweet.comment
  } else {
    text = post.text
  }
  const canHaveLinks =
    post.profile_service === 'twitter' ||
    post.profile_service === 'facebook' ||
    post.profile_service === 'threads'
  const textForLinks = text ?? ''
  const facebookLinks =
    post.profile_service === 'facebook'
      ? parseFacebookEntities(textForLinks, post.entities)
      : []
  let otherLinks: ParsedChannelLink[] = []
  if (post.profile_service === 'threads') {
    otherLinks = parseThreadsLinks(textForLinks)
  } else {
    otherLinks = parseTwitterLinks(textForLinks)
  }
  const safeOtherLinks = otherLinks.filter((link) => {
    if (!link) {
      return false
    }
    const startIdx = link.indices[0]
    const endIdx = link.indices[1]
    const hasClash = facebookLinks.some((facebookLink) => {
      const facebookStartIdx = facebookLink.indices[0]
      const facebookEndIdx = facebookLink.indices[1]
      const safe = endIdx < facebookStartIdx || startIdx > facebookEndIdx
      return !safe
    })
    return !hasClash
  })
  const linksCreator = facebookLinks
    .concat(safeOtherLinks)
    .toSorted(
      ({ indices: [startIdxA] }, { indices: [startIdxB] }) =>
        startIdxA - startIdxB,
    )
  const links = removeDuplicates(linksCreator)
  const retweetCommentLinks = canHaveLinks
    ? parseTwitterLinks(retweetComment ?? '')
    : []
  const isFixed = Boolean(post.error)
  const isPastDue = isInThePast(post.scheduled_at)
  let videoSrc
  if (
    typeof media.video === 'object' &&
    media.video?.details?.transcoded_location
  ) {
    videoSrc = media.video.details.transcoded_location
  }

  return {
    day: post.day,
    id: post.id || post._id,
    status: post?.status,
    createdAt: post.created_at,
    entities: post.entities,
    profileId: post.profile_id,
    isConfirmingDelete: post.isDeleting && !post.requestingDraftAction,
    isDeleting: post.isDeleting && post.requestingDraftAction,
    isWorking: !post.isDeleting && post.requestingDraftAction,
    isMoving: post.isMoving,
    isPastDue,
    imageSrc: isVideo ? media.thumbnail : media.picture,
    videoSrc,
    imageUrls: getImageUrls(post),
    links: canHaveLinks ? links : [],
    profileTimezone: post.profile_timezone,
    linkAttachment: {
      title: media.title,
      url: media.expanded_link,
      description: media.description,
      thumbnailUrl: media.preview,
    },
    needsApproval: post.needs_approval,
    postDetails: getPostDetails({ post }),
    profile_service: post.profile_service,
    retweet: post.retweet,
    retweetComment,
    retweetCommentLinks,
    retweetProfile: getRetweetProfileInfo(post),
    isSent: post.status === 'sent',
    isPastReminder: post.status === 'notified',
    isReminder:
      post.scheduling_type === 'reminder'
        ? true
        : post.scheduling_type === 'direct'
          ? false
          : null,
    isDraft: post.status === 'draft',
    title: post.title,
    source_url: post.source_url,
    text,
    type: getPostType({ post }),
    media,
    extra_media: post.extra_media,
    subprofile_id: post.subprofile_id,
    due_at: getPostDueAt(post),
    scheduled_at: post.scheduled_at,
    scheduledAt: post.scheduled_at,
    sharedNext: post.shared_next,
    pinned: post.pinned,
    isFixed,
    statistics: post.statistics ?? [],
    service_user_tags: post.service_user_tags,
    annotations: parseAnnotations(post?.annotations),
    user: getUser(post),
    serviceLink: post.status === 'pending_tiktok' ? null : post.service_link,
    channelData: channelDataParser(post),
    serviceUpdateId: post.service_update_id,
    dueTime: post.due_time,
    sharedBy: post.shared_by,
    campaignId: post?.campaign_id,
    campaignDetails: post.campaign_details ? post.campaign_details : null,
    notes: parseNotes(post?.notes),
    postContent: {
      imageSrc: isVideo ? media.thumbnail : media.picture,
      videoSrc,
      imageUrls: getImageUrls(post),
      linkAttachment: {
        title: media.title,
        url: media.expanded_link,
        description: media.description,
        thumbnailUrl: media.preview,
      },
      isReelPost: isReelPost({ post }),
      isFacebookReelPost: isFacebookReelPost({ post }),
      isFacebookStoryPost: isFacebookStoryPost({ post }),
      isStoryPost: isStoryPost({ post }),
      isShortPost: isShortPost({ post }),
      links: canHaveLinks ? links : [],
      retweetComment,
      retweetCommentLinks,
      retweetProfile: getRetweetProfileInfo(post),
      text,
      type: getPostType({ post }),
    },
    thread: post.thread,
    updateType: post.update_type,
    via: parseVia(post.via) || null,
    tags: post.tags || [],
  }
}

export { postParser }
export { getPostActionString }
export { parseNotes }
export default {
  postParser,
  getPostActionString,
  parseComments: parseNotes,
}
